/**
 * classify.js — filename → { role, stage, flags }.
 *
 * This is what lets the builder open a case folder and propose a report instead
 * of asking the operator to name 20 meshes by hand, and it is what assigns each
 * part its colour (see palette.js). Getting it wrong is not cosmetic: colour on
 * a surgical model implies meaning to the surgeon reading it.
 *
 * Ported from the Surgical Planning Engine's sidecar classifier
 * (apps/surgical-planning-engine/sidecar/main.py, `classify_mesh_role`), which
 * is already calibrated against verbatim team exports and documents four traps
 * that a naive rule set falls into. Kept deliberately close to it so the two do
 * not drift; REAL_EXPORTS in this module's test file mirrors the Python one.
 *
 * ⚠️ **Rules are written only against filenames that actually exist.** Every
 * pattern below was checked against three real corpora:
 *
 *   ~/Documents/Temp/Orthognathic/Case{1..4}        28 STL/OBJ, four cases
 *   ~/Documents/Temp/Orthognathic/CaseReport-Orthognathic
 *                                                   a July-2026 case, ONE OBJ
 *                                                   per report page
 *   ~/Documents/Temp/Models and Case report of FFF case
 *                                                   a free fibula flap case
 *
 * The three use genuinely different vocabularies — `Patient_Structure_Stage
 * (repaired).stl`, `Page 9 - Post plan skull.OBJ`, and `Resected Segment -
 * Mandible.OBJ` — which is exactly why none of this was written from guesses.
 */

/**
 * Lowercase, strip everything that is not a letter or digit.
 *
 * The team's separators are inconsistent — `_V2-IOS_TR` and `_V2_IOS_TR` are the
 * same flag, `MaxillaTeeth` / `Maxilla_Teeth` / `maxilla teeth` the same part —
 * and squashing makes one rule cover all of them.
 */
export const squash = (filename) =>
  String(filename ?? '')
    .replace(/^.*[/\\]/, '')
    .replace(/\.[^.]*$/, '')
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '')

/**
 * ORDER IS LOAD-BEARING. Three orderings matter and each one broke a naive rule:
 *
 *  1. `MandibleMovementSplint` is a SPLINT, not a mandible — functional outputs
 *     must be tested before anatomy, or a splint loads as an anatomical segment
 *     and gets bone colour.
 *  2. `Mandible_V2-IOS_TR` is a mandible whose teeth were replaced FROM an
 *     intraoral scan. It is not a scan. Never key an IOS role off the substring
 *     "ios" — the real scans are `restoration_*`.
 *  3. `MaxillaTeeth` must not fall through to the bare `maxilla` rule: it is a
 *     composite, and the difference decides whether it is fit to build a splint
 *     against.
 */
const ROLE_RULES = [
  // ── a numbered simulation step is a SCENE, not a part ──
  // "Page 5 - Step 1.OBJ". Must precede everything: it is whatever the plan
  // looked like at that step, and naming it by its anatomy would be wrong.
  ['step', (s) => /step\d+$/.test(s)],

  // ── functional outputs: these contain anatomy words but are not anatomy ──
  // "Mandible Resection Guide Anterior Open Type STAMPED" is a GUIDE. It
  // contains both "Mandible" and "Resection" and is neither.
  ['splint_intermediate', (s) => s.includes('splint') && s.includes('intermediate')],
  ['splint_final', (s) => s.includes('splint') && s.includes('final')],
  ['splint_movement', (s) => s.includes('splint') && s.includes('movement')],
  ['splint', (s) => s.includes('splint')],
  // 🔴 The two SPECIFIC guide roles must precede the generic one, and they exist
  // because a fibula case carries three guides at once.
  //
  // All three used to classify as bare `guide`, and the consequence was not a
  // colour problem but a RESOLUTION one: `resolve('guide','output')` sees three
  // candidates, raises a single conflict and returns null, so every guide page
  // in the report is withheld — and one operator pick could only ever choose
  // one guide for all of them. The delivered report's page 8 shows BOTH mandible
  // resection guides on one page, which that model cannot express at all.
  //
  // Measured blast radius across the test corpora: exactly the three fibula-case
  // guides change. The orthognathic genioplasty guides — "Page 20 - Genioplasty
  // fixation marking guide", "Page 21 - Genioplasty repositioning guide" — name
  // neither bone and stay `guide`, which is why the mandible test is on the word
  // and not on the procedure.
  //
  // Both roles already existed in ROLE_IMPLIED_STAGE and NON_ANATOMY_ROLES for
  // the operator's dropdown; only these rules were missing. `ORTHOGNATHIC_ROLES`
  // gained both keys in the same change, or a file that newly matches would fall
  // back to bone instead of guide white.
  //
  // ⚠️ ROLE_LABELS did NOT have them, although an earlier draft of this comment
  // said it did. The label test caught it, which is the whole reason that test
  // walks the rules rather than a hand-kept list: a role with no label is a mesh
  // the operator cannot name in the Detected panel.
  ['fibula_guide', (s) => s.includes('guide') && s.includes('fibula')],
  ['mandible_guide', (s) => s.includes('guide')
    && (s.includes('mandible') || s.includes('mandibular'))],
  ['guide', (s) => s.includes('guide') || s.includes('cuttingjig') || s.includes('jig')],
  ['alignment_tray', (s) => s.includes('alignmenttray')],

  // ── intraoral scans ──
  // "Page 2 - IntraOral scan" is a real scan; "Native skull_TR" is NOT — the TR
  // suffix means its teeth were replaced FROM one. Keying IOS off the substring
  // "ios" mislabels most of a case, so it is never used here.
  ['ios_buccal', (s) => s.includes('restoration') && s.includes('buccal')],
  ['ios_upper', (s) => s.includes('restoration') && s.includes('upper')],
  ['ios_lower', (s) => s.includes('restoration') && s.includes('lower')],
  ['ios_bite', (s) => s.includes('bite') || s.includes('occlusalregistration')],
  ['ios_upper', (s) => s.includes('upperjaw') || s.includes('upperscan')],
  ['ios_lower', (s) => s.includes('lowerjaw') || s.includes('lowerscan')],
  ['ios', (s) => s.includes('restoration') || s.includes('intraoralscan')],

  // ── free fibula flap ──
  // Ordered against the real corpus. "Resected Segment - Mandible" is the
  // RESECTED SPECIMEN (painted #FF8F8F) while "Resected Mandible" is the
  // mandible left behind — one word apart, opposite meanings, so the segment
  // rule has to come first.
  ['resection', (s) => s.includes('resectedsegment') || s.includes('resectionarea')],
  ['reconstructed_mandible', (s) => s.includes('reconstructedmandible')],
  ['fibula_segments', (s) => s.includes('fibulasegment')],
  ['fibula', (s) => s.includes('fibula')],
  ['leg', (s) => s.includes('leg')],
  ['tibia', (s) => s.includes('tibia')],
  ['blood_vessels', (s) => s.includes('bloodvessel') || s.includes('vessels')],

  // ── anatomy: nerve before mandible, composites before their bare parts ──
  ['mandibular_canal', (s) => s.includes('canal') || s.includes('nerve')],
  ['maxilla_teeth', (s) => s.includes('maxillateeth') || s.includes('maxillarteeth')],
  ['mandible_teeth', (s) => s.includes('mandibleteeth') || s.includes('mandibularteeth')],
  ['upper_teeth', (s) => s.includes('upperteeth')],
  ['lower_teeth', (s) => s.includes('lowerteeth')],
  ['genio_segment', (s) => s.includes('genio')],
  ['maxilla', (s) => s.includes('maxilla') || s.includes('upperskull')],
  ['mandible', (s) => s.includes('mandible') || s.includes('mandibular')],
  ['skull', (s) => s.includes('skull') || s.includes('cranium')],
  ['teeth', (s) => s.includes('teeth') || s.includes('tooth')],

  // ── orbital reconstruction ──────────────────────────────────────────────────
  // EVERY rule below requires an orbit word. That is deliberate and it is what
  // makes the blast radius on the orthognathic and fibula templates provably
  // zero: no filename without "orbit" in it can reach these rules, so no
  // existing case can reclassify.
  //
  // It also defuses the ordering trap rather than merely surviving it. The real
  // corpus contains `CaseA_MaxillaTeeth_TrialFitmentModel_Stamped.stl`, which
  // must stay `maxilla_teeth` — it squashes to a string holding BOTH
  // "maxillateeth" and "trialfitment". Requiring "orbit" means the trial-fitment
  // rule cannot claim it even if someone later moves this block upwards. The
  // position (after every anatomy rule) is still the belt to that braces, and
  // classify.test.js pins both.
  //
  // A bare "TrialFitmentModel.stl" therefore stays `other` exactly as it does
  // today, and the operator names it in the Detected panel.
  ['orbital_difference', (s) => s.includes('orbit') && s.includes('difference')],
  ['orbital_volume_right', (s) => s.includes('orbit') && s.includes('volume') && s.includes('right')],
  ['orbital_volume_left', (s) => s.includes('orbit') && s.includes('volume') && s.includes('left')],
  ['trial_fitment_model', (s) => s.includes('orbit') && s.includes('trialfitment')],
]

/**
 * The planning stage, which the role alone does not carry.
 *
 * `PostPlanSkull` and `Skull` are the same role and different stages, and using
 * one where the other belongs is a clinical error — so this is a separate field,
 * never folded into the name.
 */
export function classifyStage(squashed, filename = '') {
  if (squashed.includes('postplan') || squashed.includes('postop')) return 'postplan'
  // A bare "post" as its own token — `OrbitalVolumeCalculation_Rightpost.OBJ`,
  // a REAL file from the second orbital case. Without this it classified as
  // NATIVE, which put two native right orbital volumes in the library: a
  // conflict that withheld the volume page, and no post-plan volume at all.
  //
  // Tested on the ORIGINAL filename, not the squashed one, because squash strips
  // the separators that make "post" a token. `(?![a-z])` is what keeps this off
  // "Posterior" — `Mandible Resection Guide Posterior Open Type STAMPED.OBJ` is
  // in the fibula corpus and must stay stage `output`. Anchoring to the end of
  // the squashed string instead would have missed `Rightpost_v2`.
  if (/post(?![a-z])/i.test(String(filename ?? ''))) return 'postplan'
  // Free-fibula equivalents of "post plan": the anatomy after the plan acted on
  // it. "Repositioned Left Fibula" and "Reconstructed Mandible Model" are the
  // planned result, and colouring them as native would understate the plan.
  if (squashed.includes('repositioned') || squashed.includes('reconstructed')
      || squashed.includes('resected')) return 'postplan'
  if (squashed.includes('trialfitment')) return 'trial_fitment'
  if (squashed.includes('splint') || squashed.includes('guide')
      || squashed.includes('alignmenttray')) return 'output'
  if (squashed.includes('restoration') || squashed.includes('intraoralscan')) return 'ios'
  return 'native'
}

/** Non-exclusive properties worth surfacing in the UI but not worth a role. */
const FLAG_RULES = [
  // Teeth replaced from an intraoral scan. Reported as a flag, never as a role.
  // Two vocabularies write it differently: `_V2-IOS_TR` on the STL exports and a
  // bare `_TR` / ` TR` suffix on the page OBJs ("Page 9 - Native skull TR").
  // Anchored to the END so an ordinary word ending in "tr" mid-name cannot match.
  ['teeth_replaced', (s) => s.includes('iostr') || /tr$/.test(s)],
  ['cropped', (s) => s.includes('cropped')],
  ['stamped', (s) => s.includes('stamped')],
  ['repaired', (s) => s.includes('repaired')],
  ['interference_removed', (s) => s.includes('interferenceremoved')],
  ['raw', (s) => /(^|[^a-z])raw$/.test(s) || s.endsWith('raw')],
  ['cleaned', (s) => s.endsWith('clean')],
]

export function classifyFlags(squashed) {
  return FLAG_RULES.filter(([, test]) => test(squashed)).map(([flag]) => flag)
}

/**
 * A filename's words, split on separators AND on camelCase boundaries.
 *
 * `squash()` deliberately destroys both, which is right for role matching and
 * wrong for laterality — see `classifySide`. `NativeRightOrbitalVolume` has to
 * yield `right` as a word, and `Cleft` must not.
 */
export function nameTokens(filename) {
  return String(filename ?? '')
    .replace(/^.*[/\\]/, '')
    .replace(/\.[^.]*$/, '')
    // A lowercase-to-uppercase transition is a word boundary the team relies on.
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .toLowerCase()
    .split(/[^a-z0-9]+/)
    .filter(Boolean)
}

/**
 * Which side of the body the filename names: 'left', 'right', or null.
 *
 * 🔴 Matched on WORDS of the original filename, never on the squashed string.
 * Squashing removes the separators and the camelCase humps, and in this domain
 * that turns laterality into a minefield:
 *
 *   `Cleft palate`        contains "left"  — and cleft is a real craniofacial
 *                         term, so this is a filename the team could plausibly
 *                         write. A substring rule reports a LEFT-sided part.
 *   `Copyright`, `Bright` contain "right".
 *   `NativeRightOrbital`  squashes to `nativerightorbital`, where "right" is
 *                         preceded by a letter — so a word-boundary rule on the
 *                         squashed form finds nothing either.
 *
 * So a token must START with left/right rather than merely contain it.
 * `OrbitalVolumeCalculation_Rightpost.OBJ` is a real file and needs the prefix
 * match; `cleft` and `copyright` fail it because their first letters differ.
 *
 * ⚠️ `LL` and `RL` are VIEW codes — left lateral and right lateral — not
 * laterality of the part. They are two letters and match nothing here, which is
 * why the rule never looks at single or double letters.
 *
 * **Both sides named is null, not a guess.** A file saying left AND right is
 * telling us it is not one-sided, or that it is misnamed. Either way the
 * operator settles it — which is the same contract as an unmatched role.
 */
export function classifySide(filename) {
  const words = nameTokens(filename)
  const left = words.some((w) => w.startsWith('left'))
  const right = words.some((w) => w.startsWith('right'))
  if (left === right) return null              // neither, or contradictory
  return left ? 'left' : 'right'
}

/**
 * The page a file is destined for, and its step number if it is one.
 *
 * The team already encodes this. A real July-2026 case report folder is
 * organised `Page03/Page 3 - Native skull.OBJ`, `Page05/Page 5 - Step 2.OBJ`,
 * `Page20/Page 20 - Splint.OBJ` — every asset says which report page it belongs
 * to, and the multi-file pages say what each panel is.
 *
 * That is the auto-draft, already done by hand. Reading it means the builder can
 * open a folder and place every asset on the right page instead of asking the
 * operator to rebuild an ordering they already expressed.
 *
 * Returns nulls for a file that carries no page hint — the STL exports, and the
 * free-fibula assets, which are named by part rather than by page.
 *
 * @returns {{page:number|null, step:number|null, label:string|null}}
 */
export function pageHint(filename) {
  const stem = String(filename ?? '').replace(/^.*[/\\]/, '').replace(/\.[^.]*$/, '')
  const m = /^page\s*(\d+)\s*(?:-\s*(.*))?$/i.exec(stem.trim())
  if (!m) return { page: null, step: null, label: null }
  const label = (m[2] ?? '').trim() || null
  const step = label ? Number(/^step\s*(\d+)$/i.exec(label)?.[1] ?? NaN) : NaN
  return { page: Number(m[1]), step: Number.isFinite(step) ? step : null, label }
}

export function classifyRole(filename) {
  const s = squash(filename)
  for (const [role, matches] of ROLE_RULES) if (matches(s)) return role
  // Unmatched is a first-class answer. Never hide a file from the operator
  // because we could not name it — they can set its role by hand.
  return 'other'
}

/**
 * @param {string} filename
 * @returns {{role:string, stage:string, side:string|null, flags:string[]}}
 */
export function classify(filename) {
  const s = squash(filename)
  return {
    role: classifyRole(filename),
    stage: classifyStage(s, filename),
    // A single-valued field, not a flag. `flags` is a LIST of adjectives
    // (cropped, stamped, raw) and a badly-named file could carry both `left`
    // and `right` in it with nothing to say they contradict. Laterality is
    // mutually exclusive, so it gets a field that can hold only one answer —
    // or null, which is what the operator then fills in.
    side: classifySide(filename),
    flags: classifyFlags(s),
    ...pageHint(filename),
  }
}

/** Human label for a role, for the scenes panel and default captions. */
/**
 * The stage a role IMPLIES, or null when the file has to say.
 *
 * `classifyStage` reads the filename, so a descriptor-less mesh comes back
 * 'native' whatever it actually is. When the operator corrects the ROLE, leaving
 * that stage behind produces a half-corrected record. The consumers that care are
 * the `splint_intermediate` / `splint_final` / `splint_movement` recipes in
 * draft.js and pageDraft's `stage === 'output'` gate — measured, overriding a mesh
 * to `splint_intermediate` builds 7 pages with the stage derived and 6 without.
 * (Bare `splint` and `guide` happen to build the same either way, which is exactly
 * why the test for this pins an intermediate splint.)
 *
 * `mandible_guide` and `fibula_guide` are listed AND `classifyRole` now emits
 * them — see the two rules above the generic `guide` rule. They were listed here
 * before that, for the operator's dropdown alone; both paths are live now.
 *
 * Only listed here where the role settles the question by itself. A skull is
 * native or post-plan depending on the FILE, never on being a skull, so anatomy
 * is absent and its stage is left exactly as classified. Teeth are absent too:
 * a trial-fitment model and a post-plan mandible's teeth share a role.
 */
export const ROLE_IMPLIED_STAGE = {
  splint: 'output',
  splint_intermediate: 'output',
  splint_final: 'output',
  splint_movement: 'output',
  guide: 'output',
  mandible_guide: 'output',
  fibula_guide: 'output',
  alignment_tray: 'output',
  ios: 'ios',
  ios_upper: 'ios',
  ios_lower: 'ios',
  ios_buccal: 'ios',
  ios_bite: 'ios',

  // ── orbital ──
  // The difference model is a functional OUTPUT — the shape that gets made — so
  // its role settles its stage, like a splint or a guide. A trial fitment model
  // is at the trial-fitment stage by definition.
  orbital_difference: 'output',
  trial_fitment_model: 'trial_fitment',
  // orbital_volume_left / orbital_volume_right are DELIBERATELY ABSENT. A left
  // orbital volume is native or post-plan depending on the FILE, never on being
  // a left orbital volume — exactly the reasoning that keeps `skull` out of this
  // table. An implied stage here would erase the native/post-plan distinction
  // the whole volume comparison rests on.
}

/** The stage this role settles by itself, or null to keep what the file said. */
export const stageForRole = (role) => ROLE_IMPLIED_STAGE[role] ?? null

export const ROLE_LABELS = {
  skull: 'Skull',
  maxilla: 'Maxilla',
  mandible: 'Mandible',
  maxilla_teeth: 'Maxilla (teeth replaced)',
  mandible_teeth: 'Mandible (teeth replaced)',
  upper_teeth: 'Upper Teeth',
  lower_teeth: 'Lower Teeth',
  teeth: 'Teeth',
  genio_segment: 'Genioplasty Segment',
  mandibular_canal: 'Mandibular Canal',
  splint: 'Splint',
  splint_intermediate: 'Intermediate Splint',
  splint_final: 'Final Splint',
  splint_movement: 'Mandible Movement Splint',
  guide: 'Guide',
  // The two specific guide roles `classifyRole` now emits. Without a label here
  // the operator's Detected dropdown has no entry to show for a mesh the
  // classifier just named, and the label test — which walks every role the rules
  // can produce — goes red. Worded as the team names them on the real exports.
  mandible_guide: 'Mandible Resection Guide',
  fibula_guide: 'Fibula Osteotomy Guide',
  ios: 'Intraoral Scan',
  ios_upper: 'Intraoral Scan (upper)',
  ios_lower: 'Intraoral Scan (lower)',
  ios_buccal: 'Intraoral Scan (buccal)',
  ios_bite: 'Bite Registration',
  step: 'Simulation Step',
  resection: 'Resected Segment',
  reconstructed_mandible: 'Reconstructed Mandible',
  fibula_segments: 'Fibula Segments',
  fibula: 'Fibula',
  leg: 'Leg',
  tibia: 'Tibia',
  blood_vessels: 'Blood Vessels',
  alignment_tray: 'Alignment Tray',
  // Orbital. These are also what the Detected panel's dropdown offers, so the
  // wording is the operator's, not the code's.
  orbital_volume_left: 'Orbital Volume (left)',
  orbital_volume_right: 'Orbital Volume (right)',
  orbital_difference: 'Orbital Difference Model',
  trial_fitment_model: 'Trial Fitment Model',
  other: 'Unclassified',
}

export const STAGE_LABELS = {
  native: 'Native',
  postplan: 'Post-plan',
  trial_fitment: 'Trial fitment',
  output: 'Fabrication output',
  ios: 'Intraoral scan',
}

/**
 * The free-fibula rules above were written against the REAL corpus, once it was
 * readable: ~/Documents/Temp/Models and Case report of FFF case. Two of them
 * exist only because that corpus contradicted the obvious guess —
 *
 *   "Mandible Resection Guide Anterior Open Type STAMPED" is a GUIDE, though it
 *   contains both "Mandible" and "Resection".
 *
 *   "Resected Segment - Mandible" is the resected SPECIMEN (polypainted
 *   #FF8F8F) while "Resected Mandible" is the mandible left behind. One word
 *   apart, opposite meanings.
 *
 * Still unknown, because this case did not produce them: skin paddle, tibia as
 * a separate export, and segments 3-5 (this case grafted two).
 */
export const FIBULA_RULES_PENDING = false
